import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import * as React from "react"

import { cn } from "~/utils/helpers"

/**
 * Wrapper around the radio buttons in a single-select group. Hosts shared spacing.
 * Canonical intent: {@link ../design-system/intents.ts RADIO_GROUP_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return <RadioGroupPrimitive.Root className={cn("grid gap-2", className)} {...props} ref={ref} />
})
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

/**
 * A single radio button. 16x16 disc, centered dot when checked.
 * Canonical intent: {@link ../design-system/intents.ts RADIO_GROUP_ITEM_INTENT}.
 */
const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        "group peer h-4 w-4 shrink-0 cursor-pointer rounded-full border-none p-0 transition-[background-color,box-shadow]",
        "bg-component shadow-borders-base hover:bg-component-hover data-[force-hover]:bg-component-hover",
        "focus-visible:outline-none focus-visible:shadow-borders-interactive-with-shadow!",
        "data-[force-focus]:outline-none data-[force-focus]:shadow-borders-interactive-with-shadow!",
        "data-[state=checked]:bg-interactive data-[state=checked]:shadow-borders-interactive-with-shadow",
        "data-[state=checked]:hover:bg-interactive data-[state=checked]:data-[force-hover]:bg-interactive",
        "disabled:cursor-not-allowed! disabled:bg-disabled! disabled:shadow-borders-base!",
        className,
      )}
      {...props}
    >
      {/* Mounted so the dot transitions on select. */}
      <RadioGroupPrimitive.Indicator
        forceMount
        className="flex items-center justify-center scale-0 transition-[scale] duration-100 ease-overlay data-[state=checked]:scale-100"
      >
        <div className="h-1.5 w-1.5 rounded-full bg-fg-on-color group-data-[disabled]:bg-fg-disabled" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
})
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export { RadioGroup, RadioGroupItem }
